/** AddressMap — mini Leaflet mapa s pinem na adresu zakázky.
 *  Geocoduje adresu přes Nominatim (OpenStreetMap, free, no key). */

import { useEffect, useState } from "react";
import L from "leaflet";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import "leaflet/dist/leaflet.css";

const DARK_TILES = "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png";

interface AddressMapProps {
  street?: string | null;
  city?: string | null;
  zip?: string | null;
  country?: string | null;
  cityLabel?: string | null;
  height?: number;
}

function pinIcon(label?: string | null): L.DivIcon {
  return L.divIcon({
    className: "portal-map-pin",
    html: `
      <div style="position:relative;display:flex;align-items:center;gap:6px;">
        <span style="position:absolute;left:-9px;top:-9px;width:30px;height:30px;border-radius:999px;background:var(--gold);opacity:0.25;animation:portal-pulse 1.6s ease-out infinite;"></span>
        <span style="position:relative;width:12px;height:12px;border-radius:999px;background:var(--gold);border:2px solid var(--gold);box-shadow:0 0 0 2px rgba(0,0,0,0.5);"></span>
        ${label ? `<span class="badge badge-gold" style="background:var(--bg-card);font-size:11px;font-weight:500;white-space:nowrap;border:1px solid var(--gold-20);color:var(--gold-hi);padding:3px 9px;border-radius:999px;">${label}</span>` : ""}
      </div>
    `,
    iconSize: [12, 12],
    iconAnchor: [6, 6],
  });
}

async function geocode(query: string): Promise<{ lat: number; lng: number } | null> {
  try {
    const r = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(query)}`,
      { headers: { "Accept-Language": "de" } },
    );
    const arr = await r.json();
    if (!Array.isArray(arr) || !arr.length) return null;
    return { lat: Number(arr[0].lat), lng: Number(arr[0].lon) };
  } catch {
    return null;
  }
}

export function AddressMap({
  street,
  city,
  zip,
  country,
  cityLabel,
  height = 180,
}: AddressMapProps) {
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const parts = [street, [zip, city].filter(Boolean).join(" "), country || "Deutschland"]
      .filter((s) => s && s.toString().trim().length > 0)
      .map((s) => s!.toString().trim());
    const query = parts.join(", ");
    if (!query) {
      setLoading(false);
      return;
    }
    setLoading(true);
    void geocode(query).then((c) => {
      if (cancelled) return;
      if (!c) {
        // Fallback: just city + DE
        const fallback = [city, country || "Deutschland"].filter(Boolean).join(", ");
        if (fallback && fallback !== query) {
          void geocode(fallback).then((c2) => !cancelled && setCoords(c2));
        }
      } else {
        setCoords(c);
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [street, city, zip, country]);

  if (loading || !coords) {
    return (
      <div
        style={{
          borderRadius: 10,
          height,
          background: "#06090D",
          border: "1px solid var(--border)",
          marginBottom: 16,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--text-3)",
          fontSize: 12.5,
        }}
      >
        {loading ? "—" : cityLabel || city || "—"}
      </div>
    );
  }

  return (
    <div
      style={{
        borderRadius: 10,
        height,
        overflow: "hidden",
        border: "1px solid var(--border)",
        marginBottom: 16,
        position: "relative",
      }}
    >
      <MapContainer
        center={[coords.lat, coords.lng]}
        zoom={14}
        style={{ position: "absolute", inset: 0, background: "#06090D" }}
        zoomControl={false}
        attributionControl={false}
        scrollWheelZoom={false}
        dragging={false}
        doubleClickZoom={false}
      >
        <TileLayer url={DARK_TILES} />
        <Marker position={[coords.lat, coords.lng]} icon={pinIcon(cityLabel || city)} />
      </MapContainer>
    </div>
  );
}
